import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaFilter, FaPlus, FaTimes } from "react-icons/fa";
import {
  MAX_CONDITIONS,
  addCondition,
  addGroup,
  conditionCount,
  isConditionComplete,
  removeCondition,
  removeGroup,
  replaceCondition,
  resolvePath,
  setMatch,
} from "../definition";
import { conditionText } from "../labels";
import type { Catalog, ConditionSpec, FilterGroupSpec, ReportSpec } from "../types";
import ConditionEditor from "./ConditionEditor";
import { chip, chipActive, chipWarning, ghostButton, secondaryButton } from "./styles";

type Props = {
  catalog: Catalog;
  definition: ReportSpec;
  schoolYearId: string | null;
  showGroups: boolean;
  onChange: (definition: ReportSpec) => void;
};

type Editing = { groupIndex: number | null; index: number | null };

/** Conditions as readable chips; groups ("at least one of") only under Advanced. */
export default function FilterBar({ catalog, definition, schoolYearId, showGroups, onChange }: Props): ReactElement {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<Editing | null>(null);
  const full = conditionCount(definition.filter) >= MAX_CONDITIONS;

  const editor = (groupIndex: number | null, index: number | null, initial: ConditionSpec | null) => (
    <div className="absolute left-0 z-40 mt-2 w-[min(24rem,calc(100vw-2rem))] rounded-[22px] border border-slate-200 bg-white p-4 shadow-xl dark:border-white/10 dark:bg-slate-900">
      <ConditionEditor
        catalog={catalog}
        definition={definition}
        initial={initial}
        schoolYearId={schoolYearId}
        onCancel={() => setEditing(null)}
        onApply={(condition) => {
          onChange(index === null
            ? addCondition(definition, condition, groupIndex)
            : replaceCondition(definition, groupIndex, index, condition));
          setEditing(null);
        }}
      />
    </div>
  );

  const matchToggle = (group: FilterGroupSpec, groupIndex: number | null) => (
    <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300">
      <span className="sr-only">{t("reports.builder.match_label", { defaultValue: "Rows must meet" })}</span>
      <select
        value={group.match}
        onChange={(event) => onChange(setMatch(definition, groupIndex, event.target.value as "ALL" | "ANY"))}
        className="h-8 rounded-full border border-slate-200 bg-white px-2 text-xs font-bold dark:border-white/10 dark:bg-slate-950/50"
      >
        <option value="ALL">{t("reports.builder.match_all", { defaultValue: "All conditions" })}</option>
        <option value="ANY">{t("reports.builder.match_any", { defaultValue: "At least one condition" })}</option>
      </select>
    </label>
  );

  const chips = (group: FilterGroupSpec, groupIndex: number | null) => group.conditions.map((condition, index) => {
    const known = resolvePath(catalog, definition.source, condition.path) !== null;
    const complete = known && isConditionComplete(condition);
    const isEditing = editing?.groupIndex === groupIndex && editing?.index === index;
    return (
      <div key={`${condition.path}-${index}`} className="relative">
        <span className={`${chip} ${complete ? chipActive : chipWarning}`}>
          <button type="button" className="truncate text-left" onClick={() => setEditing({ groupIndex, index })}>
            {known
              ? conditionText(t, catalog, definition.source, condition)
              : t("reports.builder.condition_unavailable", { defaultValue: "Condition not available" })}
          </button>
          <button
            type="button"
            aria-label={String(t("reports.builder.remove_condition", { defaultValue: "Remove condition" }))}
            onClick={() => onChange(removeCondition(definition, groupIndex, index))}
          >
            <FaTimes aria-hidden />
          </button>
        </span>
        {isEditing && editor(groupIndex, index, condition)}
      </div>
    );
  });

  const addButton = (groupIndex: number | null) => (
    <div className="relative">
      <button
        type="button"
        className={ghostButton}
        disabled={full}
        onClick={() => setEditing({ groupIndex, index: null })}
      >
        <FaPlus aria-hidden /> {t("reports.builder.add_condition", { defaultValue: "Condition" })}
      </button>
      {editing?.groupIndex === groupIndex && editing.index === null && editor(groupIndex, null, null)}
    </div>
  );

  const groups = definition.filter.groups ?? [];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <FaFilter aria-hidden className="text-xs text-slate-400" />
        {matchToggle(definition.filter, null)}
        {chips(definition.filter, null)}
        {addButton(null)}
      </div>
      {groups.map((group, groupIndex) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={groupIndex}
          className="flex flex-wrap items-center gap-2 rounded-2xl border border-dashed border-slate-300 p-2 dark:border-white/15"
        >
          <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
            {t("reports.builder.group", { defaultValue: "Group" })}
          </span>
          {matchToggle(group, groupIndex)}
          {chips(group, groupIndex)}
          {addButton(groupIndex)}
          <button type="button" className={`${ghostButton} ml-auto`} onClick={() => onChange(removeGroup(definition, groupIndex))}>
            <FaTimes aria-hidden /> {t("reports.builder.remove_group", { defaultValue: "Remove group" })}
          </button>
        </div>
      ))}
      {showGroups && (
        <button type="button" className={secondaryButton} onClick={() => onChange(addGroup(definition))}>
          <FaPlus aria-hidden /> {t("reports.builder.add_group", { defaultValue: "Add condition group" })}
        </button>
      )}
    </div>
  );
}
